/* eslint-disable perfectionist/sort-modules */
/* eslint-disable perfectionist/sort-imports */
import { toTypedSchema } from '@vee-validate/zod';
import { debounce } from 'lodash';
import { useForm } from 'vee-validate';
import {
  computed,
  ComputedRef,
  watch,
  WatchStopHandle,
} from 'vue';
import { z } from 'zod';

import useSearchParamsState from '~/hooks/useSearchParamsState';

import { batteryFiltersSchema } from '../schemas/filtersSchema';
import { Batteries, UseBatteriesParams } from '~/types/equipment';

type FilterHandler = (_values: z.infer<typeof batteryFiltersSchema>) => void;
type FilterValues = z.infer<typeof batteryFiltersSchema>;
type FilterWatchGetter<T = unknown> = () => T;
type HookOptions = { onFiltersChange?: () => void };
type HookReturn = {
  apiParams: ComputedRef<Batteries.GetBatteriesRequest>;
  filterForm: ReturnType<typeof useForm<FilterValues>>;
  handleReset: () => void;
  handleSearch: () => Promise<void> | undefined;
  initializeForm: () => void;
  searchParams: ReturnType<typeof useSearchParamsState<UseBatteriesParams>>;
  setPage: (_value: number) => void;
  setPageSize: (_value: number) => void;
};
type BatteriesFilterForm = FilterValues;

const DEBOUNCE_DELAY = 1000;
const DEFAULT_ORDER_BY = 'created_at:desc';
const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE = 1;
const EMPTY_STRING = '';

const INITIAL_FILTER_VALUES: BatteriesFilterForm = {
  brand: EMPTY_STRING,
  is_enabled: undefined,
  model: EMPTY_STRING,
  order_by: DEFAULT_ORDER_BY,
  sku: EMPTY_STRING,
};

const INITIAL_FILTER_STATE: UseBatteriesParams = {
  ...INITIAL_FILTER_VALUES,
  page: DEFAULT_PAGE,
  page_size: DEFAULT_PAGE_SIZE,
};

export default function useBatteriesFilters(options?: HookOptions): HookReturn {
  const searchParams = useSearchParamsState<UseBatteriesParams>(
    INITIAL_FILTER_STATE,
    { parseArrays: false },
  );

  const getInitialFormValues = (): BatteriesFilterForm => ({
    brand: searchParams.value.brand || EMPTY_STRING,
    is_enabled: searchParams.value.is_enabled,
    model: searchParams.value.model || EMPTY_STRING,
    order_by: searchParams.value.order_by || DEFAULT_ORDER_BY,
    sku: searchParams.value.sku || EMPTY_STRING,
  });

  const filterForm = useForm<BatteriesFilterForm>({
    initialValues: getInitialFormValues(),
    validationSchema: toTypedSchema(batteryFiltersSchema),
  });

  const apiParams = computed(() => {
    const params = searchParams.value;
    const filteredParams: Partial<Batteries.GetBatteriesRequest> = {};

    if (params.brand?.trim()) filteredParams.brand = params.brand;
    if (params.model?.trim()) filteredParams.model = params.model;
    if (params.sku?.trim()) filteredParams.sku = params.sku;
    if (params.is_enabled !== undefined) filteredParams.is_enabled = params.is_enabled;
    if (params.order_by) filteredParams.order_by = params.order_by;

    filteredParams.page = Number(params.page) || DEFAULT_PAGE;
    filteredParams.page_size = Number(params.page_size) || DEFAULT_PAGE_SIZE;

    return filteredParams as Batteries.GetBatteriesRequest;
  });

  function applyFilters(values: FilterValues): void {
    const filteredValues: UseBatteriesParams = {
      brand: values.brand?.trim() || EMPTY_STRING,
      is_enabled: values.is_enabled,
      model: values.model?.trim() || EMPTY_STRING,
      order_by: values.order_by || DEFAULT_ORDER_BY,
      page: DEFAULT_PAGE,
      page_size: Number(searchParams.value.page_size) || DEFAULT_PAGE_SIZE,
      sku: values.sku?.trim() || EMPTY_STRING,
    };

    searchParams.value = filteredValues;
    options?.onFiltersChange?.();
  }

  const applyFiltersWithDebounce = debounce(applyFilters, DEBOUNCE_DELAY);

  const createFilterWatch = <T>(
    getter: FilterWatchGetter<T>,
    handler: FilterHandler,
  ): WatchStopHandle => watch(
      getter,
      (newValue, oldValue) => {
        if (newValue !== oldValue) {
          handler(filterForm.values);
        }
      },
    );

  createFilterWatch(() => filterForm.values.brand, applyFiltersWithDebounce);
  createFilterWatch(() => filterForm.values.model, applyFiltersWithDebounce);
  createFilterWatch(() => filterForm.values.sku, applyFiltersWithDebounce);
  createFilterWatch(() => filterForm.values.is_enabled, applyFilters);
  createFilterWatch(() => filterForm.values.order_by, applyFilters);

  function handleSearch(): Promise<void> | undefined {
    return filterForm.handleSubmit(applyFilters)();
  }

  function handleReset(): void {
    searchParams.value = { ...INITIAL_FILTER_STATE };
    filterForm.resetForm({ values: INITIAL_FILTER_VALUES });
  }

  const updateSearchParams = (updates: any): void => {
    searchParams.value = { ...searchParams.value, ...updates };
  };

  function setPage(value: number): void {
    updateSearchParams({ page: Number(value) });
  }

  function setPageSize(value: number): void {
    updateSearchParams({
      page: DEFAULT_PAGE,
      page_size: Number(value),
    });
  }

  function initializeForm(): void {
    filterForm.setValues(getInitialFormValues());
  }

  return {
    apiParams,
    filterForm,
    handleReset,
    handleSearch,
    initializeForm,
    searchParams,
    setPage,
    setPageSize,
  };
}
